import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocalParticipant, useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import {
  canLiveNativeCaption,
  canNativeCaption,
  createPcmTap,
  encodeWav,
  mergeFloat32,
  resampleTo16k,
  closeAudioContext,
} from '../../utils/nativeCaptionAudio';

const CAPTION_TOPIC = 'wl_caption';
const encoder = new TextEncoder();
const decoder = new TextDecoder();

const CallToolsContext = createContext(null);

export const useCallTools = () => useContext(CallToolsContext);

const drawContained = (ctx, video, x, y, w, h) => {
  const vw = video.videoWidth || video.clientWidth || 1;
  const vh = video.videoHeight || video.clientHeight || 1;
  const scale = Math.min(w / vw, h / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  ctx.drawImage(video, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
};

const collectAudioTracks = (room) => {
  const tracks = [];
  const push = (publication) => {
    const media = publication?.track?.mediaStreamTrack;
    if (media && media.readyState === 'live' && !publication.isMuted) {
      tracks.push(media);
    }
  };
  if (room?.localParticipant) {
    room.localParticipant.audioTrackPublications?.forEach?.(push);
  }
  room?.remoteParticipants?.forEach?.((participant) => {
    participant.audioTrackPublications?.forEach?.(push);
  });
  return tracks;
};

export const CallToolsProvider = ({ children }) => {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [captionsOn, setCaptionsOn] = useState(false);
  const [captionText, setCaptionText] = useState('');
  const [captionFrom, setCaptionFrom] = useState('');
  const [recording, setRecording] = useState(false);
  const [toast, setToast] = useState('');

  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordRafRef = useRef(0);
  const audioContextRef = useRef(null);
  const toastTimerRef = useRef(null);
  const captionTapRef = useRef(null);
  const captionLiveUnsubRef = useRef(null);
  const captionTickRef = useRef(null);
  const captionLivePushRef = useRef(false);
  const captionPcmRef = useRef([]);
  const captionBusyRef = useRef(false);
  const captionsOnRef = useRef(false);
  captionsOnRef.current = captionsOn;

  const showToast = useCallback((msg, durationMs = 3200) => {
    setToast(msg);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(''), durationMs);
  }, []);

  const publishCaption = useCallback(async (text) => {
    if (!room?.localParticipant || !text) return;
    const payload = {
      type: 'wl_caption',
      text,
      fromName: room.localParticipant.name || room.localParticipant.identity,
      fromIdentity: room.localParticipant.identity,
    };
    try {
      await room.localParticipant.publishData(encoder.encode(JSON.stringify(payload)), {
        reliable: false,
        topic: CAPTION_TOPIC,
      });
    } catch {
      // ignore
    }
  }, [room]);

  useEffect(() => {
    if (!room) return undefined;
    const onData = (payload, participant, _kind, topic) => {
      if (topic && topic !== CAPTION_TOPIC) return;
      try {
        const msg = JSON.parse(decoder.decode(payload));
        if (msg?.type !== 'wl_caption' || !msg.text) return;
        if (msg.fromIdentity && msg.fromIdentity === room.localParticipant?.identity) return;
        setCaptionText(msg.text);
        setCaptionFrom(msg.fromName || participant?.name || 'Participant');
      } catch {
        // ignore
      }
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => room.off(RoomEvent.DataReceived, onData);
  }, [room]);

  const stopNativeCallCaptions = useCallback(() => {
    captionLivePushRef.current = false;
    if (captionTickRef.current) {
      window.clearTimeout(captionTickRef.current);
      captionTickRef.current = null;
    }
    const tap = captionTapRef.current;
    captionTapRef.current = null;
    try { tap?.disconnect?.(); } catch { /* ignore */ }
    try { tap?.ownedStream?.getTracks?.().forEach((track) => track.stop()); } catch { /* ignore */ }
    try { captionLiveUnsubRef.current?.(); } catch { /* ignore */ }
    captionLiveUnsubRef.current = null;
    try { window.workloopRealm?.stopLiveCaption?.(); } catch { /* ignore */ }
    captionPcmRef.current = [];
    captionBusyRef.current = false;
  }, []);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!captionsOn) {
      try { recognitionRef.current?.stop(); } catch { /* ignore */ }
      recognitionRef.current = null;
      stopNativeCallCaptions();
      return undefined;
    }

    if (SpeechRecognition && !window.workloopRealm) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.onresult = (event) => {
        let interim = '';
        let finalText = '';
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const piece = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalText += piece;
          else interim += piece;
        }
        const text = (finalText || interim || '').trim();
        if (!text) return;
        setCaptionText(text);
        setCaptionFrom('You');
        publishCaption(text);
      };
      recognition.onend = () => {
        if (captionsOnRef.current) {
          try { recognition.start(); } catch { /* ignore */ }
        }
      };
      try {
        recognition.start();
        recognitionRef.current = recognition;
      } catch {
        showToast('Could not start captions. Allow microphone access.');
        setCaptionsOn(false);
      }
      return () => {
        try { recognition.stop(); } catch { /* ignore */ }
        recognitionRef.current = null;
      };
    }

    let cancelled = false;
    const apply = (text) => {
      const next = String(text || '').replace(/\s+/g, ' ').trim();
      if (!next || !captionsOnRef.current) return;
      setCaptionText(next);
      setCaptionFrom('You');
      publishCaption(next);
    };

    const startNative = async () => {
      const localTracks = [];
      room?.localParticipant?.audioTrackPublications?.forEach?.((publication) => {
        const media = publication?.track?.mediaStreamTrack;
        if (media && media.readyState === 'live') localTracks.push(media);
      });
      let stream = null;
      try {
        stream = localTracks.length
          ? new MediaStream(localTracks.map((track) => track.clone()))
          : await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        if (!cancelled) {
          showToast('Could not start captions. Allow microphone access.');
          setCaptionsOn(false);
        }
        return;
      }
      if (cancelled) {
        stream?.getTracks?.().forEach((track) => track.stop());
        return;
      }
      const tap = createPcmTap(stream, {
        onFloat32: (copy) => {
          if (!captionsOnRef.current) return;
          captionPcmRef.current.push(copy);
          if (captionPcmRef.current.length > 400) {
            captionPcmRef.current = captionPcmRef.current.slice(-400);
          }
        },
        shouldPushLive: () => captionLivePushRef.current && captionsOnRef.current,
      });
      if (tap) {
        tap.ownedStream = stream;
      } else {
        stream.getTracks().forEach((track) => track.stop());
      }
      captionTapRef.current = tap;

      if (canLiveNativeCaption()) {
        try {
          captionLiveUnsubRef.current = window.workloopRealm.onLiveCaption?.(apply);
          const started = await window.workloopRealm.startLiveCaption();
          if (!cancelled && started && started.ok !== false) {
            captionLivePushRef.current = true;
            return;
          }
        } catch {
          // WAV fallback below
        }
        try { captionLiveUnsubRef.current?.(); } catch { /* ignore */ }
        captionLiveUnsubRef.current = null;
        captionLivePushRef.current = false;
        try { await window.workloopRealm?.stopLiveCaption?.(); } catch { /* ignore */ }
      }

      if (cancelled) return;
      if (!canNativeCaption() || !tap) {
        showToast('Captions are not supported in this app yet. Try Chrome on the web.');
        setCaptionsOn(false);
        return;
      }

      const tick = async () => {
        if (!captionsOnRef.current) return;
        if (!captionBusyRef.current && captionPcmRef.current.length) {
          captionBusyRef.current = true;
          const rate = tap.context.sampleRate || 16000;
          const all = mergeFloat32(captionPcmRef.current);
          const keep = Math.max(1, Math.floor(rate * 4));
          const windowed = all.length > keep ? all.subarray(all.length - keep) : all;
          try {
            const text = String(await window.workloopRealm.transcribeWav(
              encodeWav(resampleTo16k(windowed, rate), 16000),
            ) || '').trim();
            if (text && captionsOnRef.current) apply(text);
          } catch {
            // ignore
          }
          captionBusyRef.current = false;
        }
        if (captionsOnRef.current && !captionLivePushRef.current) {
          captionTickRef.current = window.setTimeout(tick, 1400);
        }
      };
      captionTickRef.current = window.setTimeout(tick, 900);
    };
    startNative();
    return () => {
      cancelled = true;
      try { recognitionRef.current?.stop(); } catch { /* ignore */ }
      recognitionRef.current = null;
      stopNativeCallCaptions();
    };
  }, [captionsOn, publishCaption, room, showToast, stopNativeCallCaptions]);

  const stopRecording = useCallback(() => {
    if (recordRafRef.current) {
      window.cancelAnimationFrame(recordRafRef.current);
      recordRafRef.current = 0;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try { recorder.stop(); } catch { /* ignore */ }
      return;
    }
    if (audioContextRef.current) {
      closeAudioContext(audioContextRef.current);
      audioContextRef.current = null;
    }
    setRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    const stage = document.querySelector('.lk-workloop-call');
    if (!stage) {
      showToast('Call view is not ready to record');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(640, stage.clientWidth || 1280);
    canvas.height = Math.max(360, stage.clientHeight || 720);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      showToast('Could not start recording');
      return;
    }

    const paint = () => {
      ctx.fillStyle = '#111111';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const videos = [...stage.querySelectorAll('video')].filter(
        (video) => video.readyState >= 2 && video.clientWidth > 8 && video.clientHeight > 8,
      );
      if (videos.length === 1) {
        drawContained(ctx, videos[0], 0, 0, canvas.width, canvas.height);
      } else if (videos.length > 1) {
        const cols = Math.ceil(Math.sqrt(videos.length));
        const rows = Math.ceil(videos.length / cols);
        const cellW = canvas.width / cols;
        const cellH = canvas.height / rows;
        videos.forEach((video, index) => {
          const col = index % cols;
          const row = Math.floor(index / cols);
          drawContained(ctx, video, col * cellW, row * cellH, cellW, cellH);
        });
      }
      const overlay = stage.querySelector('canvas');
      if (overlay && overlay.width && overlay.height) {
        ctx.drawImage(overlay, 0, 0, canvas.width, canvas.height);
      }
      recordRafRef.current = window.requestAnimationFrame(paint);
    };
    paint();

    const canvasStream = canvas.captureStream(20);
    const videoTrack = canvasStream.getVideoTracks()[0];
    if (!videoTrack) {
      stopRecording();
      showToast('Could not capture call video');
      return;
    }

    const mixedTracks = [videoTrack];
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      const audioCtx = new AudioContextClass();
      audioContextRef.current = audioCtx;
      if (audioCtx.state === 'suspended') {
        try { await audioCtx.resume(); } catch { /* ignore */ }
      }
      const destination = audioCtx.createMediaStreamDestination();
      collectAudioTracks(room).forEach((track) => {
        try {
          audioCtx.createMediaStreamSource(new MediaStream([track.clone()])).connect(destination);
        } catch {
          // ignore
        }
      });
      destination.stream.getAudioTracks().forEach((track) => mixedTracks.push(track));
    }

    const composed = new MediaStream(mixedTracks);
    recordedChunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/webm');
    const recorder = new MediaRecorder(composed, { mimeType });
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data?.size) recordedChunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      if (recordRafRef.current) {
        window.cancelAnimationFrame(recordRafRef.current);
        recordRafRef.current = 0;
      }
      if (audioContextRef.current) {
        closeAudioContext(audioContextRef.current);
        audioContextRef.current = null;
      }
      const chunks = recordedChunksRef.current;
      if (chunks.length) {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `workloop-call-${Date.now()}.webm`;
        link.click();
        URL.revokeObjectURL(url);
        showToast('Recording saved');
      }
      recordedChunksRef.current = [];
      mediaRecorderRef.current = null;
      setRecording(false);
    };
    recorder.start(1000);
    setRecording(true);
    showToast('Recording this call');
  }, [room, showToast, stopRecording]);

  const toggleCaptions = useCallback(() => {
    setCaptionsOn((on) => {
      const next = !on;
      if (!next) {
        setCaptionText('');
        setCaptionFrom('');
      }
      return next;
    });
  }, []);

  const toggleRecording = useCallback(() => {
    if (recording) stopRecording();
    else startRecording();
  }, [recording, startRecording, stopRecording]);

  useEffect(() => () => {
    stopRecording();
    stopNativeCallCaptions();
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
  }, [stopNativeCallCaptions, stopRecording]);

  const value = useMemo(() => ({
    captionsOn,
    captionText,
    captionFrom,
    recording,
    toast,
    toggleCaptions,
    toggleRecording,
    localIdentity: localParticipant?.identity,
  }), [
    captionsOn,
    captionText,
    captionFrom,
    recording,
    toast,
    toggleCaptions,
    toggleRecording,
    localParticipant?.identity,
  ]);

  return (
    <CallToolsContext.Provider value={value}>
      {children}
    </CallToolsContext.Provider>
  );
};

export const CallToolsOverlays = () => {
  const tools = useCallTools();
  if (!tools) return null;
  const { recording, captionsOn, captionText, captionFrom, toast } = tools;
  return (
    <>
      {recording && (
        <div className="absolute top-3 left-3 z-40 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-600/90 text-white text-[11px] font-semibold shadow">
          <span className="relative flex h-2 w-2" aria-hidden>
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
          </span>
          REC
        </div>
      )}
      {(captionsOn || captionText) && captionText && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-40 max-w-3xl w-[90%] px-4 py-2 rounded-lg bg-black/80 text-center text-sm text-white shadow-lg pointer-events-none">
          {captionFrom ? (
            <span className="block text-[10px] uppercase tracking-wide text-white/60 mb-0.5">{captionFrom}</span>
          ) : null}
          {captionText}
        </div>
      )}
      {toast && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-40 px-3 py-1.5 rounded-lg bg-white text-black text-xs font-medium shadow">
          {toast}
        </div>
      )}
    </>
  );
};

export default CallToolsProvider;
